import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { AdminEvaluationListItem } from '@workspace/api-backend'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { Trophy } from 'lucide-react'
import { useState } from 'react'
import { PERSONA_LABELS, ScoreBadge } from '@/features/pitch'
import { useAdminEvaluationsQuery } from '@/hooks/api'
import { CellLink } from '@/utils/data-table'
import { formatFullDate } from '@/utils/format'

type SearchParams = {
  page?: number
  search?: string
}

export const Route = createFileRoute('/_protected/admin/evaluations/')({
  component: AdminEvaluationsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

const LIMIT = 20

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Preparing',
  ACTIVE: 'Ready to join',
  IN_PROGRESS: 'In session',
  INCOMPLETE: 'Incomplete',
  FAILED: 'Failed',
  COMPLETED: 'Evaluating',
}

const columns: TableColumn<AdminEvaluationListItem>[] = [
  {
    header: 'ID',
    width: '100px',
    render: (evaluation) => (
      <CellLink
        to="/admin/evaluations/$id"
        params={{ id: evaluation.id }}
        className="font-mono text-xs"
      >
        {evaluation.id.slice(0, 8)}
      </CellLink>
    ),
  },
  {
    header: 'Startup',
    render: (evaluation) => (
      <CellLink
        to="/admin/startups/$id"
        params={{ id: evaluation.startupId }}
        className="font-medium"
      >
        {evaluation.startupName ?? 'Unknown'}
      </CellLink>
    ),
  },
  {
    header: 'Founder',
    render: (evaluation) =>
      evaluation.founder ? (
        <CellLink to="/admin/users/$id" params={{ id: evaluation.founder.id }}>
          {evaluation.founder.name || evaluation.founder.email}
        </CellLink>
      ) : null,
  },
  {
    header: 'Investor',
    render: (evaluation) =>
      evaluation.investorId && evaluation.investorName ? (
        <CellLink to="/admin/investors/$id" params={{ id: evaluation.investorId }}>
          {evaluation.investorName}
        </CellLink>
      ) : null,
  },
  {
    header: 'Status',
    width: '180px',
    render: (evaluation) =>
      evaluation.status === 'COMPLETED' && evaluation.score != null ? (
        <span className="inline-flex items-center gap-1.5 text-sm">
          <Trophy className="size-3.5 text-amber-500" />
          {evaluation.winnerPersona && (
            <span className="text-muted-foreground text-xs">
              {PERSONA_LABELS[evaluation.winnerPersona] ?? evaluation.winnerPersona}
            </span>
          )}
          <ScoreBadge score={evaluation.score} size="sm" />
        </span>
      ) : (
        <CellText>{STATUS_LABELS[evaluation.status] ?? evaluation.status}</CellText>
      ),
  },
  {
    header: 'Date',
    width: '130px',
    render: (evaluation) => <CellText>{formatFullDate(evaluation.createdAt)}</CellText>,
  },
]

function AdminEvaluationsPage() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const evaluationsQuery = useAdminEvaluationsQuery({
    query: { page, limit: LIMIT, search: search || undefined },
  })

  const evaluations = evaluationsQuery.data?.data?.items ?? []
  const total = evaluationsQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-6 py-6">
      <SearchInput
        placeholder="Search by startup or investor name..."
        value={searchInput}
        onChange={setSearchInput}
        onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
      />
      <DataTable
        columns={columns}
        data={evaluations}
        isLoading={evaluationsQuery.isLoading}
        emptyMessage="No evaluations found."
        keyExtractor={(e) => e.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
